<template>
  <div class="relative" v-if="auth.user.value">
    <Button 
      variant="ghost" 
      class="flex items-center gap-3 h-auto p-2 hover:bg-muted/50 transition-all duration-200"
      @click="toggleDropdown"
    >
      <div class="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center overflow-hidden shadow-sm">
        <img 
          v-if="auth.user.value.avatar" 
          :src="auth.user.value.avatar" 
          :alt="auth.user.value.firstName"
          class="h-full w-full object-cover"
        />
        <span v-else class="text-sm font-medium text-white">
          {{ auth.user.value.firstName?.[0] }}{{ auth.user.value.lastName?.[0] }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium hidden sm:block">
          Hello {{ auth.user.value.firstName }}
        </span>
        <ChevronDown 
          :class="[
            'h-4 w-4 transition-transform duration-200',
            isDropdownOpen ? 'rotate-180' : ''
          ]" 
        />
      </div>
    </Button>
    
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDropdownOpen"
        class="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
      >
        <!-- User Info Header -->
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div class="flex items-center gap-3">
            <div class="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
              <span class="text-sm font-medium text-white">
                {{ auth.user.value.firstName?.[0] }}{{ auth.user.value.lastName?.[0] }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-medium text-gray-900 truncate">
                {{ auth.user.value.firstName }} {{ auth.user.value.lastName }}
              </div>
              <div class="text-sm text-gray-600 truncate">{{ auth.user.value.email }}</div>
              <div class="text-xs text-gray-500 mt-1">
                {{ getRoleDisplayName(auth.user.value.role) }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Menu Items -->
        <div class="py-1">
          <div 
            class="flex items-center px-4 py-3 text-sm cursor-pointer hover:bg-gray-50 transition-colors duration-150"
            @click="openProfileModal"
          >
            <Settings class="mr-3 h-4 w-4 text-gray-500" />
            <span class="text-gray-700">Profile Settings</span>
          </div>
          
          <div 
            class="flex items-center px-4 py-3 text-sm cursor-pointer hover:bg-red-50 transition-colors duration-150 text-red-600"
            @click="handleLogout"
          >
            <LogOut class="mr-3 h-4 w-4" />
            <span>Log Out</span>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script>
import { inject, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Button from './ui/button.vue'
import { ChevronDown, Settings, LogOut } from 'lucide-vue-next'

export default {
  name: 'UserProfileDropdown',
  components: {
    Button,
    ChevronDown,
    Settings,
    LogOut
  },
  emits: ['view-change'],
  setup(props, { emit }) {
    const auth = inject('auth')
    const router = useRouter()
    const isDropdownOpen = ref(false)

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value
    }

    const openProfileModal = () => {
      // Emit event to change view to profile
      emit('view-change', 'profile')
      isDropdownOpen.value = false
    }


    const handleLogout = async () => {
      try {
        await auth.logout()
        // Clear any local storage or session data
        localStorage.clear()
        sessionStorage.clear()
        // Force redirect to login page
        window.location.href = '/login'
      } catch (error) {
        console.error('Logout error:', error)
        // Even if logout fails, clear local state and redirect
        auth.clearAuthState()
        localStorage.clear()
        sessionStorage.clear()
        window.location.href = '/login'
      }
      isDropdownOpen.value = false
    }


    const getRoleDisplayName = (role) => {
      switch (role) {
        case 'employee':
          return 'Employee'
        case 'manager':
          return 'Manager'
        case 'admin':
          return 'Admin'
        default:
          return role
      }
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        isDropdownOpen.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      auth,
      isDropdownOpen,
      toggleDropdown,
      openProfileModal,
      handleLogout,
      getRoleDisplayName
    }
  }
}
</script>